import { Box, Button, ToggleButton, ToggleButtonGroup, TextField, Typography, Paper, Grid, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../Redux/store";
import { NumericFormat } from 'react-number-format';
import {
  setPayType,
  setPayAmount,
  setPayAmountMax,
  setJobType,
  setPayRate,
  setDuration,
  setDurationType,
} from "../../../Redux/slices/JobPostCreationSlice";

const BudgetStep = () => {
  const dispatch = useAppDispatch();
  const {
    payType,
    payAmount,
    payAmountMax,
    jobType,
    payRate,
    duration,
    durationType,
    regionalRates,
  } = useAppSelector((state) => state.jobPostCreation);

  const handlePayTypeChange = (_: React.MouseEvent<HTMLElement>, newPayType: 'Fixed' | 'Range' | null) => {
    if (newPayType) {
      dispatch(setPayType(newPayType));
      // Reset values when switching types
      if (newPayType === 'Fixed') {
        dispatch(setPayAmountMax(payAmount)); // Set max to min for fixed price
      }
    }
  };

  const handleJobTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newJobType: 'Short-term' | 'Internship' | 'Part-time' | 'Full-time' | null,
  ) => {
    if (newJobType !== null) {
      dispatch(setJobType(newJobType));
      if (newJobType === 'Short-term') {
        dispatch(setPayRate('One-time'));
      }
    }
  };

  return (
    <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="flex-start" justifyContent="center" minHeight="60vh" gap={6}>
      <Box minWidth={280} mb={{ xs: 4, md: 0 }}>
        <Typography variant="h4" fontWeight={600} mb={1}>
          What's your budget?
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Set your budget to find the perfect match for your project
        </Typography>

        {/* Regional Rates Section */}
        {/* <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Market Rates by Region
          </Typography>
          {regionalRates.map(({ region, rate }) => (
            <Box key={region} display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography variant="body2" color="text.secondary">
                {region}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {rate}
              </Typography>
            </Box>
          ))}
        </Paper> */}
      </Box>
      <Box flex={1} minWidth={320}>
        {/* Job Type Selection */}
        <Box mb={4}>
          <ToggleButtonGroup
            value={jobType}
            exclusive
            onChange={handleJobTypeChange}
            sx={{ 
              width: '100%',
              '& .MuiToggleButton-root': {
                px: 1.5,
                py: 0.75,
                fontSize: '0.8rem',
                border: '1px solid',
                borderColor: 'primary.main',
                color: 'primary.main',
                '&.Mui-selected': {
                  bgcolor: 'primary.light',
                  color: 'primary.dark',
                  '&:hover': {
                    bgcolor: 'primary.light',
                  }
                },
                '&:hover': {
                  bgcolor: 'primary.light',
                  color: 'primary.dark',
                }
              }
            }}
          >
            <ToggleButton value="Short-term">Short-term</ToggleButton>
            <ToggleButton value="Part-time">Part-time</ToggleButton>
            <ToggleButton value="Full-time">Full-time</ToggleButton>
            <ToggleButton value="Internship">Internship</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <ToggleButtonGroup
          value={payType}
          exclusive
          onChange={handlePayTypeChange}
          sx={{ 
            mb: 2,
            '& .MuiToggleButton-root': {
              px: 4,
              py: 1.5,
              border: '1px solid',
              borderColor: 'primary.main',
              color: 'primary.main',
              '&.Mui-selected': {
                bgcolor: 'primary.light',
                color: 'primary.dark',
                '&:hover': {
                  bgcolor: 'primary.light',
                }
              },
              '&:hover': {
                bgcolor: 'primary.light',
                color: 'primary.dark',
              }
            }
          }}
        >
          <ToggleButton value="Fixed">
            Fixed Price
          </ToggleButton>
          {/* <ToggleButton value="Range">
            Budget Range
          </ToggleButton> */}
        </ToggleButtonGroup>

        {/* Payment Amount */}
        <Box display="flex" gap={2} mb={3}>
          {payType === 'Range' ? (
            <>
              <NumericFormat
                customInput={TextField}
                label="Minimum"
                value={payAmount}
                onValueChange={(values) => {
                  dispatch(setPayAmount(values.value));
                }}
                thousandSeparator=","
                prefix="$ "
                decimalScale={0}
                allowNegative={false}
                sx={{ width: 120 }}
              />
              <NumericFormat
                customInput={TextField}
                label="Maximum"
                value={payAmountMax}
                onValueChange={(values) => {
                  dispatch(setPayAmountMax(values.value));
                }}
                thousandSeparator=","
                prefix="$ "
                decimalScale={0}
                allowNegative={false}
                sx={{ width: 120 }}
              />
            </>
          ) : (
            <NumericFormat
              customInput={TextField}
              label="Total Budget"
              value={payAmount}
              onValueChange={(values) => {
                dispatch(setPayAmount(values.value));
                dispatch(setPayAmountMax(values.value));
              }}
              thousandSeparator=","
              prefix="$ "
              decimalScale={0}
              allowNegative={false}
              sx={{ width: 200 }}
            />
          )}
        </Box>

        {/* Payment Rate */}
        {jobType !== 'Short-term' && (
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Payment Frequency</InputLabel>
            <Select
              value={payRate}
              label="Payment Frequency"
              onChange={(e) => dispatch(setPayRate(e.target.value))}
            >
              <MenuItem value="One-time">One-time</MenuItem>
              <MenuItem value="Hourly">Hourly</MenuItem>
              <MenuItem value="Weekly">Weekly</MenuItem>
              <MenuItem value="Bi-weekly">Bi-weekly</MenuItem>
              <MenuItem value="Monthly">Monthly</MenuItem>
              <MenuItem value="Annual">Annual</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Duration */}
        {jobType === 'Internship' && (
          <Box display="flex" gap={2} mb={3}>
            <TextField
              label="Duration"
              value={duration}
              onChange={e => dispatch(setDuration(e.target.value))}
              type="number"
              sx={{ width: 120 }}
            />
            <FormControl sx={{ width: 120 }}>
              <InputLabel>Unit</InputLabel>
              <Select
                value={durationType}
                label="Unit"
                onChange={(e) => dispatch(setDurationType(e.target.value))}
              >
                <MenuItem value="hours">Hours</MenuItem>
                <MenuItem value="days">Days</MenuItem>
                <MenuItem value="weeks">Weeks</MenuItem>
                <MenuItem value="months">Months</MenuItem>
                <MenuItem value="years">Years</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" mb={2}>
          Complex projects may require higher budgets.
        </Typography>
      </Box>
    </Box>
  );
};

export default BudgetStep; 